import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Check, Users, Building2, Globe, ArrowLeft, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    description: 'Try structured change coaching',
    priceMonthly: 0,
    icon: Users,
    features: [
      '5 coaching sessions per month',
      'Basic change guidance',
      'Change lifecycle framework',
      'Preview outputs (exec summary only)',
    ]
  },
  {
    id: 'individual',
    name: 'Individual',
    description: 'For change practitioners',
    priceMonthly: 29,
    priceYearly: 290,
    icon: Users,
    features: [
      'Unlimited coaching sessions',
      'Basic challenge mode',
      'Change lifecycle guidance',
      'Standard outputs',
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Structured Change Coaching',
    priceMonthly: 59,
    priceYearly: 590,
    icon: Users,
    popular: true,
    features: [
      'Everything in Individual',
      'Structured challenge mode',
      'Framework application (ADKAR, impact logic)',
      'Full exportable outputs',
      'Stakeholder logic prompts',
      'Implementation vs build challenge',
      'Executive-ready deliverables',
    ]
  },
  {
    id: 'team',
    name: 'Team',
    description: 'For internal change teams',
    priceYearly: 4900,
    icon: Building2,
    features: [
      'Everything in Pro',
      '5-20 team members',
      'Shared workspace',
      'Governance templates',
      'Change load tracking',
      'Priority support',
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Enterprise Change Intelligence',
    priceYearly: null, // Contact
    icon: Globe,
    features: [
      'Everything in Team',
      'Unlimited users',
      'Portfolio load modelling',
      'Executive sequencing',
      'Saturation analysis',
      'Custom integrations',
      'Dedicated support',
    ]
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [loading, setLoading] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);

  const handleSubscribe = async (tierId) => {
    if (!user) {
      navigate('/register?redirect=pricing');
      return;
    }

    if (tierId === 'free') {
      navigate('/dashboard');
      return;
    }

    if (tierId === 'enterprise') {
      window.location.href = 'mailto:james@slpartners.au?subject=Enterprise%20Enquiry';
      return;
    }

    setLoading(tierId);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        tier: tierId,
        billing_period: billingPeriod,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleRedeemCode = async () => {
    if (!user) {
      navigate('/register?redirect=pricing');
      return;
    }

    if (!accessCode.trim()) return;

    try {
      const response = await axios.post(`${API}/subscription/redeem-code`, {
        code: accessCode
      });
      toast.success(`Code redeemed! You now have ${response.data.tier} access.`);
      setAccessCode('');
      setShowCodeInput(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-slate-900">SLP Change Coach</h1>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* Hero */}
      <div className="bg-slate-900 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Structured Change Coaching
          </h2>
          <p className="text-lg text-slate-300 mb-2">
            Not an AI assistant. A thinking partner that challenges your logic.
          </p>
          <p className="text-sm text-slate-400 mb-8">
            If it prevents one poorly framed change plan, it pays for itself.
          </p>
          
          {/* Billing toggle */}
          <div className="inline-flex items-center bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                billingPeriod === 'monthly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                billingPeriod === 'yearly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Yearly <span className="text-emerald-400 ml-1">Save 2 months</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="max-w-7xl mx-auto px-4 py-12 -mt-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const price = billingPeriod === 'yearly' 
              ? tier.priceYearly 
              : tier.priceMonthly;
            
            return (
              <div 
                key={tier.id}
                className={`bg-white rounded-2xl border-2 p-6 ${
                  tier.popular 
                    ? 'border-amber-400 shadow-lg relative' 
                    : 'border-slate-200'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    tier.popular ? 'bg-amber-100' : 'bg-slate-100'
                  }`}>
                    <Icon className={`h-6 w-6 ${tier.popular ? 'text-amber-600' : 'text-slate-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{tier.name}</h3>
                    <p className="text-sm text-slate-500">{tier.description}</p>
                  </div>
                </div>

                <div className="mb-6">
                  {tier.id === 'free' ? (
                    <>
                      <span className="text-3xl font-bold text-slate-900">Free</span>
                      <p className="text-xs text-slate-400 mt-1">No credit card required</p>
                    </>
                  ) : price ? (
                    <>
                      <span className="text-3xl font-bold text-slate-900">
                        ${price.toLocaleString()}
                      </span>
                      <span className="text-slate-500">
                        /{billingPeriod === 'yearly' ? 'year' : 'month'}
                      </span>
                      <p className="text-xs text-slate-400 mt-1">AUD + GST</p>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-slate-900">Contact Us</span>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={loading === tier.id}
                  className={`w-full ${
                    tier.popular 
                      ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' 
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                >
                  {loading === tier.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : tier.id === 'free' ? (
                    'Start Free'
                  ) : tier.id === 'enterprise' ? (
                    'Contact Sales'
                  ) : (
                    'Get Started'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Access code section */}
        <div className="mt-12 text-center">
          {showCodeInput ? (
            <div className="max-w-sm mx-auto">
              <p className="text-sm text-slate-600 mb-3">Enter your access code</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="ACCESS CODE"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-center font-mono uppercase focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <Button onClick={handleRedeemCode}>
                  Redeem
                </Button>
              </div>
              <button 
                onClick={() => setShowCodeInput(false)}
                className="text-sm text-slate-400 hover:text-slate-600 mt-3"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCodeInput(true)}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              Have an access code?
            </button>
          )}
        </div>

        {/* FAQ or trust signals */}
        <div className="mt-16 text-center">
          <p className="text-slate-500">
            Questions? Email{' '}
            <a href="mailto:james@slpartners.au" className="text-slate-900 underline">
              james@slpartners.au
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
